import { LegendItem } from "@app/projects/network-assessment/maps/network-coverage-map/interfaces";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { MapCustomControlProps } from "@app/shared/mapping/MapCustomControlProps";
import { useMapboxControl } from "@app/shared/mapping/useMapBoxControl";
import { FC } from "react";
import { createPortal } from "react-dom";

/** Prop-driven ZIP legend. No data context (deliberate — ZIP holds map state as lifted
 *  useState in ZipCoverageMapRoute). Entries are supplied by the caller as layers render;
 *  member/provider point layers arrive in 202283 / 202310, so today both arrays are empty
 *  and each section shows its empty state. */
export interface ZipCoverageMapLegendControlProps extends MapCustomControlProps {
  members: LegendItem[];
  providers: LegendItem[];
}

const EMPTY_TEXT = "No active data layers";

const LegendSection: FC<{ title: string; items: LegendItem[] }> = ({ title, items }) => (
  <div>
    <span className="provider-member-layer-control__title">{title}</span>
    <div className="provider-member-layer-control__layers-container">
      {items.length > 0 ? items.map((item, index) => (
        <div key={index} className="provider-member-layer-control__item">
          <QesIcon
            className="me-2"
            height="1rem"
            iconName={item.icon}
            style={{ fill: item.color }}
            isDecorative
            width="1rem"
          />
          <span className="provider-member-layer-control__value">{item.label}</span>
        </div>
      )) :
        <span className="provider-member-layer-control__value">{EMPTY_TEXT}</span>
      }
    </div>
  </div>
);

const ZipCoverageMapLegendControl: FC<ZipCoverageMapLegendControlProps> = ({
  members, providers, position, ignoreInMapExport, mapRef
}) => {
  const container = useMapboxControl({ mapRef, position });
  if (!container) return null;

  return createPortal(
    <div className="legend-control-container" data-testid="zip-coverage-map-legend" {...(ignoreInMapExport ? { 'data-html2canvas-ignore': '' } : {})}>
      <LegendSection title="Members" items={members} />
      <LegendSection title="Providers" items={providers} />
    </div>,
    container
  )
}

export default ZipCoverageMapLegendControl;